import {
    required,
    TextField,
    ReferenceField,
    Show,
    SimpleShowLayout,
    ReferenceManyField,
    NumberField,
    Datagrid,
    useTranslate,
    usePermissions,
    DeleteButton,
    useRecordContext,
} from "react-admin";
import { Box, Button, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import EditIcon from "@mui/icons-material/Edit";

export const ViewSkuArrival = (props) => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();

    if (isLoading) {
        return <></>;
    }

    return (
        <Show
            resource="arrivals"
            id={props.arrivalData?.id}
            sx={{
                "& .RaShow-card": { background: "inherit", boxShadow: "none" },
                "& .MuiPaper-root": { maxWidth: "100%" },
            }}
            actions={false}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    mb: 2,
                }}
            >
                <Typography variant="body1">{translate("equip.viewArrival")}</Typography>
                <Button
                    onClick={() => props.setPrintLabels(true)}
                    variant="text"
                    startIcon={<PrintIcon />}
                    title={translate("equip.printLabels")}
                    aria-label={translate("equip.printLabels")}
                >
                    {translate("equip.printLabels")}
                </Button>
            </Box>

            <SimpleShowLayout edit={false}>
                <TextField
                    label="equip.purchaseOrderNumber"
                    source="purchaseOrderNumber"
                    validate={[required()]}
                />
                <NumberField
                    label="equip.purchasePricePerItem"
                    source="purchasePrice"
                    options={{ style: "currency", currency: "EUR" }}
                />
                <RaDateField label="equip.orderDate" source="orderDate" validate={[required()]} />
                <RaDateField
                    label="equip.deliveryDate"
                    source="deliveryDate"
                    validate={[required()]}
                />
                <ReferenceField
                    label="equip.supplier"
                    source="supplier.id"
                    reference="suppliers"
                    link={false}
                >
                    <TextField source="name" />
                </ReferenceField>
            </SimpleShowLayout>
            <Box
                sx={{
                    m: 2,
                    overflowX: "auto",
                    maxHeight: "35vh"
                }}>
                <ReferenceManyField reference="skus" addLabel={false} perPage={50} target="arrival">
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            "& .RaDatagrid-headerCell": { backgroundColor: "inherit" },
                            "& th": { zIndex: 10, backgroundColor: "#393939!important" },
                        }}
                    >
                        <TextField label="equip.labelNumber" source="labelNumber" />
                        <TextField label="equip.manufacturer" source="manufacturer.name" />
                        <TextField label="equip.supplier" source="supplier.name" />
                        <NumberField
                            label="equip.purchasePrice"
                            source="arrival.purchasePrice"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                        />
                        <NumberField
                            label="equip.currentPrice"
                            source="currentPrice"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                            sortable={false}
                        />
                        <NumberField
                            label="equip.weight"
                            source="weight"
                            options={{
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                style: "unit",
                                unit: "kilogram",
                            }}
                        />
                        <TextField
                            label="equip.serialNumber"
                            source="serialNumber"
                            emptyText="N/A"
                        />
                        <TextField label="equip.comment" source="comment" />
                        <EditButton title={translate("equip.edit")} {...props} />
                        {permissions.vote({
                            resource: "skus",
                            attribute: "delete",
                        }) && (
                            <DeleteButton
                                redirect={false}
                                title={translate("equip.delete")}
                                aria-label={translate("equip.delete")}
                            />
                        )}
                    </Datagrid>
                </ReferenceManyField>
            </Box>
            <Box sx={{ display: "flex", mx: 2, mb: 2, gap: 3 }}>
                {permissions.vote({
                    resource: "skus",
                    attribute: "create",
                    subject: props.skuRecord,
                }) && (
                    <Button
                        onClick={() => {
                            props.setDeliveryWindow("create");
                        }}
                        title={translate("equip.addSkus")}
                        aria-label={translate("equip.addSkus")}
                    >
                        {translate("equip.addSkus")}
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    background: "inherit",
                    p: 3,
                }}
            >
                {permissions.vote({
                    resource: "arrivals",
                    attribute: "edit",
                    subject: props.arrivalData,
                }) && (
                    <Box>
                        <Button
                            size="small"
                            variant="contained"
                            redirect={false}
                            onClick={() => props.setDeliveryWindow("edit")}
                            title={translate("ra.action.edit")}
                            aria-label={translate("ra.action.edit")}
                        >
                            {translate("ra.action.edit")}
                        </Button>
                    </Box>
                )}
                <Box>
                    <Button
                        onClick={() => {
                            props.setDeliveryWindow();
                            props.setArrivalData();
                        }}
                        title={translate("equip.close")}
                        aria-label={translate("equip.close")}
                    >
                        {translate("equip.close")}
                    </Button>
                </Box>
            </Box>
        </Show>
    );
};

const EditButton = (props) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const edit = (e) => {
        e.stopPropagation();
        props.setDeliveryWindow("create");
        props.setSkuRecord(record);
    };
    return (
        <Button
            startIcon={<EditIcon />}
            size="small"
            variant="text"
            onClick={edit}
            title={translate("ra.action.edit")}
            aria-label={translate("ra.action.edit")}
        >
            {translate("ra.action.edit")}
        </Button>
    );
};
